"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

import Reveal from "./Reveal";
import { useLanguage } from "../i18n/LanguageProvider";
import {
  PENGUMUMAN_BPJS as P,
  ambilTeks,
  kodeBahasa,
  sudahBerlaku,
} from "../data/pengumuman";

/* ============================================================
   SECTION PENGUMUMAN BPJS - BERANDA

   Dipasang di antara panel Cari Dokter dan section Faskes Tingkat I.

   ============================================================
   REVISI: POSTER JADI ISI UTAMA

   Versi sebelumnya dua kolom - teks di kiri, poster kecil di kanan.
   Versi ini poster di tengah sebagai isi utama, sesuai permintaan.

   Susunannya mengikuti pola section Klinik Kirana tepat di bawahnya:
   label kecil, judul, satu kalimat, lalu gambar besar. Dua section
   bersebelahan dengan pola sama terbaca sebagai satu keluarga; kalau
   yang satu dua kolom dan yang lain terpusat, keduanya terlihat
   seperti dikerjakan dua orang berbeda.

   YANG SENGAJA DIPERTAHANKAN: <h2> dan satu kalimat sebagai teks
   HTML di atas poster. Bukan untuk mengulang isi poster - kalimatnya
   memang cuma satu - tapi karena section tanpa heading akan dilompati
   pembaca layar, dan mesin pencari tidak melihat apa pun selain
   sebuah berkas gambar. Poster A4 penuh teks tidak bisa dibaca
   keduanya.

   Kalau ingin benar-benar poster telanjang, hapus blok <h2> dan <p>
   di bawah. Kodenya tetap jalan; yang hilang cuma dua hal barusan.
   ============================================================ */

export default function PengumumanReservasiBPJS() {
  const ctx = useLanguage();
  const kode = kodeBahasa(ctx);

  /* Judul berganti sendiri setelah 1 Oktober: "Mulai 1 Oktober..."
     jadi kalimat yang tidak lagi menyebut tanggal. Dihitung setelah
     menempel, bukan saat render peladen, supaya HTML keduanya sama
     dan tidak memicu hydration error. */
  const [berlaku, setBerlaku] = useState(false);
  useEffect(() => setBerlaku(sudahBerlaku(new Date())), []);

  const judul = ambilTeks(
    berlaku ? P.judul.sesudahMulai : P.judul.sebelumMulai,
    kode
  );

  return (
    <section className="bg-[var(--c-surface)] px-4 pt-16 sm:px-6 sm:pt-20 md:pt-24 lg:px-10">
      {/* max-w-7xl menyamakan tepi dengan panel Cari Dokter di atasnya
          dan section Klinik Kirana di bawahnya. */}
      <div className="mx-auto max-w-7xl">
        <Reveal>

          {/* ---------- LABEL ---------- */}
          {/* Huruf kapital berjarak, meniru "FASKES TINGKAT I" di
              section berikutnya. Bukan heading - ia label kategori,
              jadi ditulis sebagai <p>. Dibuat <h3> justru merusak
              urutan heading halaman. */}
          <p className="mb-2 text-center text-xs font-semibold uppercase tracking-[0.18em] text-[#0f6a3b] sm:text-sm">
            {ambilTeks({ id: "Informasi Penting", en: "Important Notice" }, kode)}
          </p>

          {/* ---------- JUDUL ---------- */}
          {/* h2, sejajar dengan "Klinik Kirana" dan "Layanan Kami". */}
          <h2 className="mx-auto max-w-4xl text-center text-2xl font-bold leading-[1.15] tracking-tight text-[var(--c-ink)] sm:text-3xl md:text-4xl">
            {judul}
          </h2>

          {/* ---------- SATU KALIMAT ---------- */}
          <p className="mx-auto mt-4 max-w-3xl text-center text-sm leading-relaxed text-[var(--c-body)] sm:text-base">
            {ambilTeks(P.ringkas, kode)}
          </p>

          {/* ---------- POSTER ---------- */}
          {/* Lebar dibatasi 520px. Poster A4 tegak tanpa batas lebar
              akan setinggi 1,4x lebarnya - di layar 1280px itu berarti
              blok setinggi 1800px, dan pengunjung harus menggulir tiga
              layar penuh untuk melewatinya. Pada 520px tingginya
              sekitar 735px: masih dominan, masih terbaca, tidak
              menelan halaman.

              Bisa diklik dan menuju halaman detail - orang yang
              tertarik pada posternya biasanya ingin tahu lanjutannya,
              dan gambar yang tidak bisa diklik memaksanya mencari
              tombol. */}
          <Link
            href={P.tautan}
            className="group mt-8 block sm:mt-10"
            /* tabIndex -1 + aria-hidden: tautan ini menuju halaman yang
               SAMA dengan tombol Selengkapnya di bawahnya. Dibiarkan
               bisa difokus, pengguna keyboard menekan Tab dua kali
               untuk tujuan identik dan pembaca layar membacakan tautan
               yang sama dua kali. Klik tetikus tetap berfungsi. */
            tabIndex={-1}
            aria-hidden="true"
          >
            <Image
              src={P.poster.src}
              width={P.poster.lebar}
              height={P.poster.tinggi}
              alt={ambilTeks(P.poster.alt, kode)}
              /* priority TIDAK dipasang. Section ini di bawah hero, jadi
                 posternya belum terlihat saat halaman dibuka. Memberi
                 priority berarti ia berebut bandwidth dengan gambar hero
                 yang justru elemen LCP halaman. */
              sizes="(min-width: 640px) 520px, 100vw"
              className="mx-auto w-full max-w-[520px] rounded-2xl border border-[var(--c-line)] shadow-[0_8px_24px_-8px_rgba(18,22,26,0.25)] transition-transform duration-300 group-hover:scale-[1.01]"
            />
          </Link>

          {/* ---------- SELENGKAPNYA ---------- */}
          {/* Teks tombol menyebut apa yang menunggu di baliknya, bukan
              kata "Selengkapnya" saja. Pengunjung yang sudah paham
              aturannya tidak perlu menekan; yang belum tahu caranya,
              tahu persis apa yang akan ia dapat. */}
          <div className="mt-6 flex justify-center sm:mt-8">
            <Link
              href={P.tautan}
              className="inline-flex items-center gap-2 rounded-full bg-[#0f6a3b] px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#0c5730] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0f6a3b] sm:text-base"
            >
              {ambilTeks(
                { id: "Selengkapnya & cara mendaftar", en: "Read more & how to register" },
                kode
              )}
              <ArrowRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </Link>
          </div>

        </Reveal>
      </div>
    </section>
  );
}
